import { prefersReducedMotion } from '@theme/utilities';

const DESKTOP_QUERY = '(min-width: 750px)';
/** Start loading as the card approaches the viewport; play once any part is visible. */
const OBSERVER_THRESHOLDS = [0, 0.01, 0.1, 0.25, 0.5, 0.75, 1];
const PRELOAD_ROOT_MARGIN = '15% 0px 15% 0px';
/** Cap simultaneous video network loads so one section cannot saturate bandwidth. */
const MAX_CONCURRENT_LOADS = 2;

class ProductShowcaseCarousel extends HTMLElement {
  /** @type {boolean} */
  #initialized = false;

  /** @type {MediaQueryList | null} */
  #desktopQuery = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Map<Element, { ratio: number, intersecting: boolean }>} */
  #visibility = new Map();

  /** @type {WeakMap<HTMLVideoElement, Promise<void>>} */
  #playPromises = new WeakMap();

  /** @type {WeakSet<HTMLVideoElement>} */
  #sourcesAttached = new WeakSet();

  /** @type {Set<HTMLVideoElement>} */
  #loadingVideos = new Set();

  /** @type {HTMLVideoElement[]} */
  #loadQueue = [];

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.#bindMuteButtons();
    this.#prepareVideos();
    this.#observe();
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#desktopQuery?.removeEventListener('change', this.#onBreakpointChange);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#loadQueue = [];
    this.#loadingVideos.clear();
    this.#items.forEach((item) => this.#pause(item, true));
  }

  #onBreakpointChange = () => {
    this.#observe();
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#items.forEach((item) => this.#pause(item));
      return;
    }
    this.#syncPlayback();
  };

  /**
   * @returns {HTMLElement[]}
   */
  get #items() {
    return Array.from(this.querySelectorAll('[data-showcase-item]'));
  }

  /**
   * @param {Element} item
   * @returns {HTMLVideoElement | null}
   */
  #videoFor(item) {
    const video = item.querySelector('video.product-showcase__video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  #prepareVideos() {
    this.#items.forEach((item) => {
      const video = this.#videoFor(item);
      if (!video) return;

      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.setAttribute('preload', 'none');
      video.controls = false;
      video.disablePictureInPicture = true;
      if ('disableRemotePlayback' in video) {
        video.disableRemotePlayback = true;
      }

      // Ensure sources never start until we deliberately attach them.
      video.querySelectorAll('source').forEach((source) => {
        if (!(source instanceof HTMLSourceElement)) return;
        const url = source.getAttribute('src') || source.dataset.src;
        if (!url) return;
        source.dataset.src = url;
        source.removeAttribute('src');
      });

      video.addEventListener('playing', () => item.classList.add('is-playing'));
      video.addEventListener('pause', () => {
        if (video.ended || video.currentTime === 0) item.classList.remove('is-playing');
      });
      video.addEventListener('emptied', () => item.classList.remove('is-playing'));
      video.addEventListener('loadeddata', () => this.#onVideoLoadProgress(video), { once: false });
      video.addEventListener('canplay', () => this.#onVideoLoadProgress(video), { once: false });
      video.addEventListener('error', () => this.#onVideoLoadProgress(video), { once: false });
    });
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #onVideoLoadProgress(video) {
    if (!this.#loadingVideos.has(video)) return;
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA || video.error) {
      this.#loadingVideos.delete(video);
      this.#drainLoadQueue();
    }
  }

  #bindMuteButtons() {
    this.querySelectorAll('[data-showcase-mute]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const item = button.closest('[data-showcase-item]');
        if (!item) return;
        this.#toggleMute(item, button);
      });
    });
  }

  /**
   * @param {Element} item
   * @param {HTMLButtonElement} button
   */
  #toggleMute(item, button) {
    const video = this.#videoFor(item);
    if (!video) return;

    const shouldUnmute = video.muted;
    this.#items.forEach((other) => {
      const otherVideo = this.#videoFor(other);
      const otherButton = other.querySelector('[data-showcase-mute]');
      if (!otherVideo) return;

      if (other === item && shouldUnmute) {
        otherVideo.muted = false;
        other.setAttribute('data-unmuted', 'true');
        if (otherButton instanceof HTMLButtonElement) {
          otherButton.setAttribute('aria-pressed', 'true');
          otherButton.setAttribute('aria-label', button.dataset.muteLabel || 'Mute video');
        }
      } else {
        otherVideo.muted = true;
        other.removeAttribute('data-unmuted');
        if (otherButton instanceof HTMLButtonElement) {
          otherButton.setAttribute('aria-pressed', 'false');
          otherButton.setAttribute('aria-label', otherButton.dataset.unmuteLabel || 'Unmute video');
        }
      }
    });
  }

  #observe() {
    this.#observer?.disconnect();
    this.#visibility.clear();

    const track = this.querySelector('[data-showcase-track]');
    const isDesktop = this.#desktopQuery?.matches ?? false;

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#visibility.set(entry.target, {
            ratio: entry.intersectionRatio,
            intersecting: entry.isIntersecting,
          });
        });
        this.#syncPlayback();
      },
      {
        root: isDesktop ? null : track instanceof HTMLElement ? track : null,
        rootMargin: PRELOAD_ROOT_MARGIN,
        threshold: OBSERVER_THRESHOLDS,
      }
    );

    this.#items.forEach((item) => {
      if (this.#videoFor(item)) this.#observer?.observe(item);
    });

    this.#primeInitiallyVisible();
    this.#syncPlayback();
  }

  /** Mark cards already on screen before the first observer callback. */
  #primeInitiallyVisible() {
    this.#items.forEach((item) => {
      const video = this.#videoFor(item);
      if (!video || !this.#isLikelyInView(item)) return;

      this.#visibility.set(item, { ratio: 1, intersecting: true });
    });
  }

  /**
   * @param {Element} item
   * @returns {boolean}
   */
  #isLikelyInView(item) {
    const track = this.querySelector('[data-showcase-track]');
    const rect = item.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;

    const isDesktop = this.#desktopQuery?.matches ?? false;
    if (isDesktop || !(track instanceof HTMLElement)) {
      const viewport = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
      return (
        rect.right > viewport.left &&
        rect.left < viewport.right &&
        rect.bottom > viewport.top &&
        rect.top < viewport.bottom
      );
    }

    const root = track.getBoundingClientRect();
    return rect.right > root.left && rect.left < root.right && rect.bottom > root.top && rect.top < root.bottom;
  }

  #syncPlayback() {
    if (prefersReducedMotion() || document.hidden) {
      this.#items.forEach((item) => this.#pause(item));
      return;
    }

    this.#items.forEach((item) => {
      const video = this.#videoFor(item);
      if (!video) return;

      const state = this.#visibility.get(item);
      const inView = state?.intersecting === true && (state.ratio ?? 0) > 0;

      if (inView) {
        this.#requestWarm(video);
        this.#play(item);
      } else {
        this.#pause(item);
        if ((state?.ratio ?? 0) === 0) this.#cooldown(video);
      }
    });
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #attachSources(video) {
    if (this.#sourcesAttached.has(video)) return false;

    let attached = false;
    video.querySelectorAll('source').forEach((source) => {
      if (!(source instanceof HTMLSourceElement)) return;
      const url = source.dataset.src;
      if (!url || source.getAttribute('src')) return;
      source.src = url;
      attached = true;
    });

    if (attached) {
      this.#sourcesAttached.add(video);
    }
    return attached;
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #requestWarm(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.#warmNow(video);
      return;
    }

    if (this.#loadingVideos.has(video) || this.#loadQueue.includes(video)) {
      return;
    }

    if (this.#loadingVideos.size < MAX_CONCURRENT_LOADS) {
      this.#warmNow(video);
      return;
    }

    this.#loadQueue.push(video);
  }

  #drainLoadQueue() {
    while (this.#loadingVideos.size < MAX_CONCURRENT_LOADS && this.#loadQueue.length > 0) {
      const next = this.#loadQueue.shift();
      if (!next) continue;

      const stillWanted = this.#items.some((item) => {
        const video = this.#videoFor(item);
        if (video !== next) return false;
        const state = this.#visibility.get(item);
        return state?.intersecting === true && (state.ratio ?? 0) > 0;
      });

      if (stillWanted) this.#warmNow(next);
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #warmNow(video) {
    const attached = this.#attachSources(video);
    if (video.preload !== 'auto') {
      video.preload = 'auto';
    }

    if (attached || video.readyState === HTMLMediaElement.HAVE_NOTHING) {
      this.#loadingVideos.add(video);
      try {
        video.load();
      } catch {
        this.#loadingVideos.delete(video);
      }
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #cooldown(video) {
    if (!video.paused) return;

    this.#loadQueue = this.#loadQueue.filter((queued) => queued !== video);
    this.#loadingVideos.delete(video);

    if (video.preload === 'none') return;
    video.preload = 'none';
  }

  /**
   * @param {Element} item
   */
  #play(item) {
    const video = this.#videoFor(item);
    if (!video) return;

    this.#requestWarm(video);

    if (!video.paused && !video.ended) return;
    if (this.#playPromises.has(video)) return;

    const start = () => {
      if (!video.paused && !video.ended) return;
      const playPromise = video.play();
      if (playPromise === undefined) return;

      this.#playPromises.set(video, playPromise);
      playPromise
        .catch(() => {
          item.classList.remove('is-playing');
        })
        .finally(() => {
          this.#playPromises.delete(video);
        });
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      start();
      return;
    }

    video.addEventListener('loadeddata', start, { once: true });
  }

  /**
   * @param {Element} item
   * @param {boolean} [reset]
   */
  #pause(item, reset = false) {
    const video = this.#videoFor(item);
    if (!video) return;

    if (!video.paused) {
      video.pause();
    }

    if (reset || video.currentTime === 0 || video.ended) {
      item.classList.remove('is-playing');
    }

    if (reset) {
      try {
        video.currentTime = 0;
      } catch {
        // Ignore seek errors on unloaded media.
      }
      video.preload = 'none';
    }
  }
}

if (!customElements.get('product-showcase-carousel')) {
  customElements.define('product-showcase-carousel', ProductShowcaseCarousel);
}
